import React, { useCallback, useMemo, useState } from 'react'

import { SquareKanban } from 'lucide-react'
import Link from 'next/link'
import { HiArrowDownTray } from 'react-icons/hi2'
import { LuPencilLine } from 'react-icons/lu'
import { toast } from 'sonner'
import { twMerge } from 'tailwind-merge'

import Button from '@/components/common/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/common/DropdownMenu'
import IconButtonV2 from '@/components/common/IconButtonV2'
import {
  OrgPermissionGate,
  ResourcePermissionGate,
} from '@/components/common/PermissionGate'
import { Skeleton } from '@/components/common/Skeleton'
import Spinner from '@/components/common/Spinner'
import {
  Tooltip,
  TooltipContent,
  type TooltipContentProps,
  TooltipTrigger,
} from '@/components/common/TooltipV3'
import {
  SalesforceConnectionDialog,
  type SalesforceConnectionFormValues,
  buildSalesforceProviderConfig,
  getSalesforceConnectionDefaultValues,
  getSalesforceEnvironment,
  getSalesforceLoginHost,
} from '@/components/integrations/Salesforce/ConnectionSettings'
import { FieldMapperDialog } from '@/components/integrations/Salesforce/FieldMapper'
import { SalesforceIcon } from '@/components/integrations/Salesforce/Icon'
import { ManualSyncDialog } from '@/components/integrations/Salesforce/ManualSyncDialog'
import { StageMapperDialog } from '@/components/integrations/Salesforce/StageMapper'

import { useIsAtLeastAdmin } from '@/hooks/billing/useBillingRole'
import { useMCPServers } from '@/hooks/mcp/useMCPServers'
import {
  useEnableMCPServer,
  useSalesforceOrgServer,
  useUpdateMCPServer,
} from '@/hooks/mcp/useOrganizationMCPServers'
import { useStoreMCPOAuthTokens } from '@/hooks/mcp/useStoreMCPOAuthTokens'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { usePursuit } from '@/hooks/pursuits/usePursuit'
import { usePursuits } from '@/hooks/pursuits/usePursuits'
import { useCanEnableSalesforce } from '@/hooks/salesforce/useCanEnableSalesforce'
import {
  useHasSalesforceIntegration,
  useSyncSalesforceStatusWithThrow,
  useSyncSalesforceWithThrow,
} from '@/hooks/salesforce/useSalesforceIntegration'
import { useSyncPursuitsToSalesforceStatus } from '@/hooks/salesforce/useSyncPursuitsToSalesforceStatus'

import type { UUID } from '@/types/Id'
import type { CreatePursuit, Pursuit } from '@/types/pursuit/Pursuit'

interface SalesforceDropdownProps {
  className?: string
  iconOnly?: boolean
  iconOnlyVariant?: 'default' | 'outline-compact'
  tooltipContentProps?: TooltipContentProps
}

const SalesforceDropdown: React.FC<SalesforceDropdownProps> = ({
  className,
  iconOnly,
  iconOnlyVariant = 'default',
  tooltipContentProps,
}) => {
  const { activeOrgId } = useActiveOrgId()

  const isAtLeastAdmin = useIsAtLeastAdmin()

  const [stageMapperOpen, setStageMapperOpen] = useState(false)
  const [fieldMapperOpen, setFieldMapperOpen] = useState(false)

  const { refetch: refetchPursuits } = usePursuits({
    orgId: activeOrgId,
  })

  const handleSuccess = useCallback(() => {
    refetchPursuits()
  }, [refetchPursuits])

  const {
    data: syncFromSalesforceStatus,
    isLoading: isLoadingSalesforceStatus,
  } = useSyncSalesforceStatusWithThrow({
    orgId: activeOrgId,
    onSuccess: handleSuccess,
  })

  const { mutate: syncFromSalesforce } = useSyncSalesforceWithThrow({
    orgId: activeOrgId,
  })

  const isSyncingFromSalesforce =
    !!syncFromSalesforceStatus &&
    (syncFromSalesforceStatus.status === 'pending' ||
      syncFromSalesforceStatus.status === 'running')

  const handleManageStageMappings = useCallback(() => {
    setStageMapperOpen(true)
  }, [setStageMapperOpen])

  const handleManageFieldMappings = useCallback(() => {
    setFieldMapperOpen(true)
  }, [setFieldMapperOpen])

  const handleSyncFromSalesforce = () => {
    if (isSyncingFromSalesforce || isLoadingSalesforceStatus) {
      return
    }
    syncFromSalesforce()
    toast.info(
      'Syncing opportunities from Salesforce. This may take some time...',
    )
  }

  return (
    <>
      {activeOrgId && (
        <StageMapperDialog
          orgId={activeOrgId}
          open={stageMapperOpen}
          onOpenChange={setStageMapperOpen}
        />
      )}
      {activeOrgId && (
        <FieldMapperDialog
          orgId={activeOrgId}
          open={fieldMapperOpen}
          onOpenChange={setFieldMapperOpen}
        />
      )}
      <DropdownMenu>
        {iconOnly ? (
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild={true}>
              <DropdownMenuTrigger asChild={true}>
                {iconOnlyVariant === 'outline-compact' ? (
                  <Button
                    variant="outline-compact"
                    className={twMerge(
                      'shadow-sm active:scale-100 !px-0 w-7 flex-shrink-0',
                      className,
                    )}
                  >
                    <SalesforceIcon size={16} />
                  </Button>
                ) : (
                  <IconButtonV2
                    className={twMerge(
                      'inline-flex items-center justify-center',
                      'w-7 h-7',
                      className,
                    )}
                  >
                    <SalesforceIcon size={16} />
                  </IconButtonV2>
                )}
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent {...tooltipContentProps}>
              Manage Salesforce
            </TooltipContent>
          </Tooltip>
        ) : (
          <DropdownMenuTrigger asChild={true}>
            <Button
              variant="outline-compact"
              className={twMerge('shadow-sm active:scale-100', className)}
            >
              <SalesforceIcon size={16} />
              <span>Manage Salesforce</span>
            </Button>
          </DropdownMenuTrigger>
        )}
        <DropdownMenuContent align="start" className="min-w-[200px]">
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild={true}>
              <DropdownMenuItem
                className="flex items-center gap-2"
                onClick={handleManageFieldMappings}
                disabled={!isAtLeastAdmin}
              >
                <LuPencilLine
                  size={16}
                  className="flex-shrink-0 text-primary-700 dark:text-primary-300"
                />
                <span>Map Opportunity Fields</span>
              </DropdownMenuItem>
            </TooltipTrigger>
            {!isAtLeastAdmin && (
              <TooltipContent className="max-w-[256px]" side="left">
                <span>
                  Only organization administrators can edit Salesforce settings.
                </span>
              </TooltipContent>
            )}
          </Tooltip>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild={true}>
              <DropdownMenuItem
                className="flex items-center gap-2"
                onClick={handleManageStageMappings}
                disabled={!isAtLeastAdmin}
              >
                <SquareKanban
                  size={16}
                  className="flex-shrink-0 text-primary-700 dark:text-primary-300"
                />
                <span>Map Pipeline Stages</span>
              </DropdownMenuItem>
            </TooltipTrigger>
            {!isAtLeastAdmin && (
              <TooltipContent className="max-w-[256px]" side="left">
                <span>
                  Only organization administrators can edit Salesforce settings.
                </span>
              </TooltipContent>
            )}
          </Tooltip>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="flex items-center gap-2"
            disabled={isSyncingFromSalesforce || isLoadingSalesforceStatus}
            onClick={handleSyncFromSalesforce}
          >
            {isSyncingFromSalesforce ? (
              <>
                <Spinner className="flex-shrink-0 w-[15px] h-[15px]" />
                <span>Syncing from Salesforce...</span>
              </>
            ) : (
              <>
                <HiArrowDownTray
                  size={16}
                  className="flex-shrink-0 text-primary-700 dark:text-primary-300"
                />
                <span>Sync from Salesforce</span>
              </>
            )}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  )
}

interface ConnectSalesforceButtonProps {
  className?: string
  iconOnly?: boolean
  iconOnlyVariant?: 'default' | 'outline-compact'
  onConnected: () => void
  tooltipContentProps?: TooltipContentProps
}

const ConnectSalesforceButton: React.FC<ConnectSalesforceButtonProps> = ({
  className,
  iconOnly,
  iconOnlyVariant = 'default',
  onConnected,
  tooltipContentProps,
}) => {
  const { activeOrgId } = useActiveOrgId()
  const { data: salesforceOrgServer, isLoading: isLoadingOrgServer } =
    useSalesforceOrgServer({
      orgId: activeOrgId,
    })
  const { data: mcpServers, isLoading: isLoadingMcpServers } = useMCPServers({
    orgId: activeOrgId,
  })
  const { mutateAsync: enableMCPServer, isPending: isEnablingMCPServer } =
    useEnableMCPServer({
      orgId: activeOrgId,
    })
  const { mutateAsync: updateMCPServer, isPending: isUpdatingMCPServer } =
    useUpdateMCPServer({
      orgId: activeOrgId,
    })

  const canEnableSalesforce = useCanEnableSalesforce()
  const connectTooltip = 'Connect Salesforce'
  const disabledTooltip =
    'Please contact support or your account manager to enable your Salesforce integration.'

  const [setupOpen, setSetupOpen] = useState<boolean>(false)
  const [isAuthenticating, setIsAuthenticating] = useState<boolean>(false)

  const salesforceConnectionDefaultValues = useMemo(
    () =>
      getSalesforceConnectionDefaultValues(
        salesforceOrgServer?.oauth_provider_config || null,
      ),
    [salesforceOrgServer?.oauth_provider_config],
  )

  const handleStoreStart = useCallback(() => {
    setIsAuthenticating(true)
  }, [setIsAuthenticating])

  const handleStoreError = useCallback(() => {
    setIsAuthenticating(false)
  }, [setIsAuthenticating])

  const handleTokensStored = useCallback(() => {
    setIsAuthenticating(false)
    setSetupOpen(false)
    onConnected()
  }, [onConnected, setIsAuthenticating, setSetupOpen])

  const { startOAuthFlow } = useStoreMCPOAuthTokens({
    orgId: activeOrgId,
    orgServer: salesforceOrgServer,
    onStoreStart: handleStoreStart,
    onStoreError: handleStoreError,
    onTokensStored: handleTokensStored,
  })

  const handleSetupSalesforce = useCallback(() => {
    setSetupOpen(true)
  }, [setSetupOpen])

  const handleConnectSalesforce = useCallback(
    async (values: SalesforceConnectionFormValues) => {
      const providerConfig = buildSalesforceProviderConfig({
        baseProviderConfig: salesforceOrgServer?.oauth_provider_config || null,
        values,
      })

      if (!providerConfig) {
        toast.error('Enter a valid Salesforce login domain.')
        return
      }

      setIsAuthenticating(true)
      try {
        let orgServer = salesforceOrgServer
        if (!orgServer) {
          const salesforceMcpServer = mcpServers?.find(
            (server) => server.server_name === 'salesforce',
          )
          if (!salesforceMcpServer) {
            toast.error(
              'Salesforce OAuth is not available for this organization.',
            )
            setIsAuthenticating(false)
            return
          }
          if (!salesforceMcpServer.has_managed_credentials) {
            toast.error(
              'Salesforce OAuth credentials are not configured for this organization.',
            )
            setIsAuthenticating(false)
            return
          }
          orgServer = await enableMCPServer({
            mcpServerId: salesforceMcpServer.id,
            request: {
              oauth_provider_config: providerConfig,
            },
          })
        } else {
          const currentLoginHost = getSalesforceLoginHost(
            orgServer.oauth_provider_config,
          )
          const currentEnvironment = getSalesforceEnvironment(
            orgServer.oauth_provider_config,
          )
          if (
            currentLoginHost !== providerConfig.login_host ||
            currentEnvironment !== providerConfig.salesforce_environment
          ) {
            orgServer = await updateMCPServer({
              mcpServerId: orgServer.mcp_server_id,
              request: {
                oauth_provider_config: providerConfig,
              },
            })
          }
        }

        await startOAuthFlow({ orgServer })
      } catch (error) {
        setIsAuthenticating(false)
        toast.error('Failed to connect to Salesforce. Please try again.')
      }
    },
    [
      enableMCPServer,
      mcpServers,
      salesforceOrgServer,
      startOAuthFlow,
      updateMCPServer,
    ],
  )

  const disabled =
    isLoadingOrgServer ||
    isLoadingMcpServers ||
    isEnablingMCPServer ||
    isUpdatingMCPServer ||
    isAuthenticating ||
    !canEnableSalesforce

  const iconOnlyButton =
    iconOnlyVariant === 'outline-compact' ? (
      <Button
        variant="outline-compact"
        disabled={disabled}
        onClick={handleSetupSalesforce}
        className={twMerge(
          'shadow-sm active:scale-100 !px-0 w-7 flex-shrink-0',
          className,
        )}
      >
        <SalesforceIcon size={16} />
      </Button>
    ) : (
      <IconButtonV2
        tooltip={canEnableSalesforce ? connectTooltip : disabledTooltip}
        tooltipContentProps={tooltipContentProps}
        disabled={disabled}
        onClick={handleSetupSalesforce}
        className={className}
      >
        <SalesforceIcon size={16} />
      </IconButtonV2>
    )

  return (
    <OrgPermissionGate permission="admin" fallback="hide">
      <SalesforceConnectionDialog
        defaultValues={salesforceConnectionDefaultValues}
        isSubmitting={
          isAuthenticating || isEnablingMCPServer || isUpdatingMCPServer
        }
        onOpenChange={setSetupOpen}
        onSubmit={handleConnectSalesforce}
        open={setupOpen}
      />
      {iconOnly &&
      iconOnlyVariant === 'outline-compact' &&
      canEnableSalesforce ? (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild={true}>{iconOnlyButton}</TooltipTrigger>
          <TooltipContent {...tooltipContentProps}>
            {connectTooltip}
          </TooltipContent>
        </Tooltip>
      ) : (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild={true}>
            {iconOnly ? (
              iconOnlyButton
            ) : (
              <Button
                variant="outline-compact"
                disabled={disabled}
                onClick={handleSetupSalesforce}
                className={twMerge('shadow-sm', className)}
              >
                <SalesforceIcon size={16} />
                <span>Connect to Salesforce</span>
              </Button>
            )}
          </TooltipTrigger>
          {!canEnableSalesforce &&
          (!iconOnly || iconOnlyVariant === 'outline-compact') ? (
            <TooltipContent className="max-w-[300px]">
              <span>{disabledTooltip}</span>
            </TooltipContent>
          ) : null}
        </Tooltip>
      )}
    </OrgPermissionGate>
  )
}

interface SalesforceButtonProps {
  className?: string
  iconOnly?: boolean
  iconOnlyVariant?: 'default' | 'outline-compact'
  tooltipContentProps?: TooltipContentProps
}

const SalesforceButtonInner: React.FC<SalesforceButtonProps> = ({
  className,
  iconOnly,
  iconOnlyVariant = 'default',
  tooltipContentProps,
}) => {
  const { activeOrgId } = useActiveOrgId()
  const {
    data: hasSalesforceIntegration,
    isLoading: isLoadingSalesforceIntegration,
    refetch: refetchIntegration,
  } = useHasSalesforceIntegration({
    orgId: activeOrgId,
  })

  const [postConnectStageMapperOpen, setPostConnectStageMapperOpen] =
    useState(false)

  const handleConnected = useCallback(() => {
    setPostConnectStageMapperOpen(true)
    refetchIntegration()
  }, [refetchIntegration])

  const handlePostConnectStageMapperOpenChange = useCallback(
    (open: boolean) => {
      setPostConnectStageMapperOpen(open)
      if (!open) {
        refetchIntegration()
      }
    },
    [refetchIntegration],
  )

  return (
    <>
      {activeOrgId ? (
        <StageMapperDialog
          orgId={activeOrgId}
          open={postConnectStageMapperOpen}
          onOpenChange={handlePostConnectStageMapperOpenChange}
        />
      ) : null}
      {isLoadingSalesforceIntegration ? null : hasSalesforceIntegration ? (
        <SalesforceDropdown
          className={className}
          iconOnly={iconOnly}
          iconOnlyVariant={iconOnlyVariant}
          tooltipContentProps={tooltipContentProps}
        />
      ) : (
        <ConnectSalesforceButton
          className={className}
          iconOnly={iconOnly}
          iconOnlyVariant={iconOnlyVariant}
          onConnected={handleConnected}
          tooltipContentProps={tooltipContentProps}
        />
      )}
    </>
  )
}

export const SalesforceButton: React.FC<SalesforceButtonProps> = ({
  className,
  iconOnly,
  iconOnlyVariant,
  tooltipContentProps,
}) => {
  return (
    <OrgPermissionGate permission="admin" fallback="hide">
      <SalesforceButtonInner
        className={className}
        iconOnly={iconOnly}
        iconOnlyVariant={iconOnlyVariant}
        tooltipContentProps={tooltipContentProps}
      />
    </OrgPermissionGate>
  )
}

interface SyncToSalesforceButtonProps {
  pursuitId: UUID
  onSuccess?: () => void
  iconOnly?: boolean
  className?: string
}

export const SyncToSalesforceButton: React.FC<SyncToSalesforceButtonProps> = ({
  pursuitId,
  onSuccess,
  iconOnly,
  className,
}) => {
  const { activeOrgId } = useActiveOrgId()
  const {
    data: hasSalesforceIntegration,
    isLoading: isLoadingSalesforceIntegration,
    refetch: refetchIntegration,
  } = useHasSalesforceIntegration({
    orgId: activeOrgId,
  })

  const {
    data: syncToSalesforceStatus,
    isLoading: isLoadingSyncToSalesforceStatus,
  } = useSyncPursuitsToSalesforceStatus({
    orgId: activeOrgId,
    onSuccess,
  })

  const [manualSyncDialogOpen, setManualSyncDialogOpen] =
    useState<boolean>(false)

  const isRunning =
    syncToSalesforceStatus &&
    (syncToSalesforceStatus.status === 'pending' ||
      syncToSalesforceStatus.status === 'running')

  const canEnableSalesforce = useCanEnableSalesforce()
  const disabled =
    isRunning ||
    isLoadingSalesforceIntegration ||
    isLoadingSyncToSalesforceStatus ||
    !canEnableSalesforce ||
    !hasSalesforceIntegration

  const handleOpenManualSyncDialog = useCallback(() => {
    setManualSyncDialogOpen(true)
  }, [setManualSyncDialogOpen])

  const handleConnected = useCallback(() => {
    refetchIntegration()
  }, [refetchIntegration])

  if (canEnableSalesforce && !hasSalesforceIntegration) {
    return (
      <ConnectSalesforceButton
        iconOnly={iconOnly}
        className={className}
        onConnected={handleConnected}
      />
    )
  }

  return (
    <>
      {activeOrgId && (
        <ManualSyncDialog
          orgId={activeOrgId}
          from={{
            type: 'pursuit',
            pursuitId,
          }}
          open={manualSyncDialogOpen}
          onOpenChange={setManualSyncDialogOpen}
        />
      )}
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild={true}>
          {iconOnly ? (
            <IconButtonV2
              tooltip={isRunning ? 'Syncing...' : 'Sync to Salesforce'}
              disabled={disabled}
              onClick={handleOpenManualSyncDialog}
              className={twMerge('shadow-sm', className)}
            >
              {isRunning ? (
                <Spinner className="flex-shrink-0 w-[15px] h-[15px]" />
              ) : (
                <SalesforceIcon size={16} />
              )}
            </IconButtonV2>
          ) : (
            <Button
              variant="outline-compact"
              className="shadow-sm"
              disabled={disabled}
              onClick={handleOpenManualSyncDialog}
            >
              <SalesforceIcon size={18} />
              {isRunning ? (
                <span>Syncing...</span>
              ) : (
                <span>Sync to Salesforce</span>
              )}
            </Button>
          )}
        </TooltipTrigger>
        {!canEnableSalesforce && (
          <TooltipContent className="max-w-[256px]">
            <span>
              Please contact support or your account manager to enable your
              Salesforce integration.
            </span>
          </TooltipContent>
        )}
        {!hasSalesforceIntegration && canEnableSalesforce && (
          <TooltipContent className="max-w-[256px]">
            <span>Please connect to Salesforce to enable syncing.</span>
          </TooltipContent>
        )}
      </Tooltip>
    </>
  )
}

interface ViewInSalesforceButtonProps {
  pursuitId: UUID
  iconOnly?: boolean
  className?: string
}

const ViewInSalesforceButtonInner: React.FC<ViewInSalesforceButtonProps> = ({
  pursuitId,
  iconOnly,
  className,
}) => {
  const { activeOrgId } = useActiveOrgId()
  const {
    data: pursuit,
    isLoading: isLoadingPursuit,
    refetch: refetchPursuit,
  } = usePursuit({
    pursuitId,
  })
  const {
    data: hasSalesforceIntegration,
    isLoading: isLoadingSalesforceIntegration,
    refetch: refetchIntegration,
  } = useHasSalesforceIntegration({
    orgId: activeOrgId,
  })

  const handleConnected = useCallback(() => {
    refetchIntegration()
  }, [refetchIntegration])

  if (isLoadingPursuit || isLoadingSalesforceIntegration) {
    return <Skeleton className={iconOnly ? 'w-7 h-7' : 'w-[163px] h-7'} />
  }

  if (!pursuit) {
    return null
  }

  if (
    pursuit.provider !== 'salesforce' ||
    !pursuit.salesforce_opportunity_url
  ) {
    if (!hasSalesforceIntegration) {
      return (
        <ConnectSalesforceButton
          iconOnly={iconOnly}
          className={className}
          onConnected={handleConnected}
        />
      )
    }

    return (
      <OrgPermissionGate permission="write" fallback="hide">
        <ResourcePermissionGate
          resourceType="pursuit"
          resourceId={pursuitId}
          permission="write"
          fallback="hide"
        >
          <SyncToSalesforceButton
            pursuitId={pursuitId}
            onSuccess={refetchPursuit}
            iconOnly={iconOnly}
            className={className}
          />
        </ResourcePermissionGate>
      </OrgPermissionGate>
    )
  }

  if (iconOnly) {
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <Link
            href={pursuit.salesforce_opportunity_url}
            target="_blank"
            className="flex"
          >
            <Button
              variant="outline-compact"
              className={twMerge('w-7 h-7 shadow-sm', className)}
            >
              <SalesforceIcon size={16} />
            </Button>
          </Link>
        </TooltipTrigger>
        <TooltipContent>View in Salesforce</TooltipContent>
      </Tooltip>
    )
  }

  return (
    <Link
      href={pursuit.salesforce_opportunity_url}
      target="_blank"
      className="flex"
    >
      <Button variant="outline-compact">
        <SalesforceIcon size={18} />
        <span>View in Salesforce</span>
      </Button>
    </Link>
  )
}

export const ViewInSalesforceButton: React.FC<ViewInSalesforceButtonProps> = ({
  pursuitId,
  iconOnly,
  className,
}) => {
  return (
    <OrgPermissionGate permission="read" fallback="hide">
      <ViewInSalesforceButtonInner
        pursuitId={pursuitId}
        iconOnly={iconOnly}
        className={className}
      />
    </OrgPermissionGate>
  )
}

interface AddOpportunityToSalesforceButtonProps {
  req: CreatePursuit
  pursuitId?: UUID
  onSuccess?: (pursuit: Pursuit) => void
}

export const AddOpportunityToSalesforceButton: React.FC<
  AddOpportunityToSalesforceButtonProps
> = ({ req, pursuitId, onSuccess }) => {
  const { activeOrgId } = useActiveOrgId()

  const canEnableSalesforce = useCanEnableSalesforce()

  const [delayedManualSyncDialogOpen, setDelayedManualSyncDialogOpen] =
    useState<boolean>(false)
  const [manualSyncDialogOpen, setManualSyncDialogOpen] =
    useState<boolean>(false)

  const {
    data: hasSalesforceIntegration,
    isLoading: isLoadingSalesforceIntegration,
    refetch: refetchIntegration,
  } = useHasSalesforceIntegration({
    orgId: activeOrgId,
  })

  const handleAddToSalesforce = useCallback(() => {
    setManualSyncDialogOpen(true)
    setDelayedManualSyncDialogOpen(true)
  }, [setManualSyncDialogOpen])

  const handleManualSyncDialogOpenChange = useCallback(
    (open: boolean) => {
      setManualSyncDialogOpen(open)
      if (!open) {
        // delay the dialog open to avoid sudden closing for the user
        setTimeout(() => {
          setDelayedManualSyncDialogOpen(false)
        }, 200)
      }
    },
    [setManualSyncDialogOpen, setDelayedManualSyncDialogOpen],
  )

  const handleConnected = useCallback(() => {
    refetchIntegration()
  }, [refetchIntegration])

  if (isLoadingSalesforceIntegration) {
    return <Skeleton className="w-[159px] h-7" />
  }

  if (!hasSalesforceIntegration) {
    return <ConnectSalesforceButton onConnected={handleConnected} />
  }

  if (pursuitId) {
    return <ViewInSalesforceButton pursuitId={pursuitId} />
  }

  return (
    <>
      {activeOrgId && delayedManualSyncDialogOpen && (
        <ManualSyncDialog
          orgId={activeOrgId}
          from={{
            type: 'create_pursuit',
            create_pursuit_req: req,
          }}
          open={manualSyncDialogOpen}
          onOpenChange={handleManualSyncDialogOpenChange}
          onCreatePursuitSuccess={onSuccess}
        />
      )}
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild={true}>
          <Button
            variant="outline-compact"
            disabled={!canEnableSalesforce}
            onClick={handleAddToSalesforce}
            className="min-w-24"
          >
            <SalesforceIcon size={18} />
            <span>Add to Salesforce</span>
          </Button>
        </TooltipTrigger>
        {!canEnableSalesforce && (
          <TooltipContent className="max-w-[256px]">
            <span>
              Please contact support or your account manager to enable your
              Salesforce integration.
            </span>
          </TooltipContent>
        )}
      </Tooltip>
    </>
  )
}
